import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useForm } from '@mantine/form';
import {
    createPayoutCascadesUrl,
    isNewPayoutCascadeEntityId,
    type PayoutCascadeTemplate,
    type PayoutGroup,
    PayoutCascadeQueueItemType,
    useCreatePayoutCascade,
    useDeletePayoutCascade,
    usePayoutCascade,
    usePayoutCascadeGroupsList,
    usePayoutCascadeTemplatesList,
    useUpdatePayoutCascadeV2,
} from '~/entities/payout';
import { type OrderedEntry } from '~/shared/ui/OrderedEntryList';
import { createQueueEntry, cascadeToQueueEntries, queueEntriesToPayload } from './queue';
import { type CascadeEditorFormValues, type QueueEntryValue } from './types';
import { cascadeToFormValues, formValuesToFilter, initialFormValues } from './transformValues';
import { validate } from './validate';

export const useCascadeEditor = (id?: string) => {
    const navigate = useNavigate();
    const isNew = isNewPayoutCascadeEntityId(id);

    const cascade = usePayoutCascade({ 'id.value': id ?? '' });
    const groups = usePayoutCascadeGroupsList({
        'pagination.page': '0',
        'pagination.perPage': '1000',
    });
    const templates = usePayoutCascadeTemplatesList({
        'pagination.page': '0',
        'pagination.perPage': '1000',
    });

    const [isLoaded, setIsLoaded] = useState(isNew);
    const [queueEntries, setQueueEntries] = useState<OrderedEntry<QueueEntryValue>[]>([]);

    const form = useForm<CascadeEditorFormValues>({
        validate,
        initialValues: initialFormValues,
    });

    useEffect(() => {
        if (isLoaded || !cascade.data?.cascade) {
            return;
        }

        form.setValues(cascadeToFormValues(cascade.data.cascade));
        setQueueEntries(cascadeToQueueEntries(cascade.data.cascade));
        setIsLoaded(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded, cascade.data?.cascade]);

    const groupOptions = (groups.data?.items ?? [])
        .filter((group): group is PayoutGroup & { id: { value: string } } => Boolean(group.id?.value))
        .map((group) => ({ value: group.id.value, label: group.name?.value || group.id.value }));

    const templateOptions = (templates.data?.items ?? [])
        .filter((template): template is PayoutCascadeTemplate & { id: { value: string } } => Boolean(template.id?.value))
        .map((template) => ({ value: template.id.value, label: template.name?.value || template.id.value }));

    const groupLabelById = new Map(groupOptions.map((option) => [option.value, option.label]));
    const templateLabelById = new Map(templateOptions.map((option) => [option.value, option.label]));

    const create = useCreatePayoutCascade({
        onSuccess: () => navigate(createPayoutCascadesUrl()),
    });

    const updateV2 = useUpdatePayoutCascadeV2();

    const deleteCascade = useDeletePayoutCascade({
        onSuccess: () => navigate(createPayoutCascadesUrl()),
    });

    const handleAddGroup = (groupId: string) => {
        const name = groupLabelById.get(groupId) ?? groupId;

        setQueueEntries((prev) => [...prev, createQueueEntry(PayoutCascadeQueueItemType.QUEUE_ITEM_TYPE_GROUP, groupId, name)]);
    };

    const handleAddTemplate = (templateId: string) => {
        const name = templateLabelById.get(templateId) ?? templateId;

        setQueueEntries((prev) => [
            ...prev,
            createQueueEntry(PayoutCascadeQueueItemType.QUEUE_ITEM_TYPE_TEMPLATE, templateId, name),
        ]);
    };

    const handleClearQueue = () => setQueueEntries([]);

    const handleSubmit = form.onSubmit((values) => {
        const filter = formValuesToFilter(values);
        const queueItems = queueEntriesToPayload(queueEntries);

        if (isNew) {
            create.mutate({ name: { value: values.name }, filter, queueItems });

            return;
        }

        updateV2.mutate({ id: { value: id }, name: { value: values.name }, filter, queueItems });
    });

    const handleDelete = () => {
        if (!id) {
            return;
        }

        deleteCascade.mutate({ 'id.value': id });
    };

    return {
        isNew,
        isLoading: !isNew && cascade.isPending,
        form,
        queueEntries,
        setQueueEntries,
        groupOptions,
        templateOptions,
        isQueueOptionsLoading: groups.isPending || templates.isPending,
        handleAddGroup,
        handleAddTemplate,
        handleClearQueue,
        handleSubmit,
        handleDelete,
        isSubmitPending: create.isPending || updateV2.isPending,
        isDeletePending: deleteCascade.isPending,
    };
};
